import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SERVICES_CONSTANTS } from '../utils/constants';
import { StringUtils } from '../utils/stringUtils';
import { ApiResponse } from '../model/auth/api-response';
import { WorkbiePayoutSummaryResponseDTO } from '../model/workbie-payout-summary-dto';
import { WorkbiePayoutDetailDTO } from '../model/workbie-payout-detail-dto';
import { ProcessPaymentRequestDTO, ProcessPaymentResponseDTO } from '../model/process-payment-dto';
import {
  WorkbiePayoutsSummaryDTO,
  WorkbiePendingListResponseDTO,
  WorkbiePaidListResponseDTO
} from '../model/workbie-payout-dashboard-dto';

@Injectable({
  providedIn: 'root'
})
export class WorkbiePayoutsService {
  private baseUrl: string = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private stringUtils: StringUtils
  ) {}

  /**
   * Obtener resumen general del dashboard de pagos (tarjetas superiores)
   */
  getSummary(): Observable<ApiResponse<WorkbiePayoutsSummaryDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.SUMMARY
    );

    return this.http.get<ApiResponse<WorkbiePayoutsSummaryDTO>>(url);
  }

  /**
   * Obtener listado paginado de TODOS los pagos pendientes (pestaña "Pendientes")
   * NOTA: a diferencia de getWeeklyPendingPayouts (1-based), este endpoint es 0-based
   * (pageNumber=0 es la primera página).
   */
  getPending(pageNumber: number = 0, pageSize: number = 10): Observable<ApiResponse<WorkbiePendingListResponseDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.PENDING
    );

    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ApiResponse<WorkbiePendingListResponseDTO>>(url, { params });
  }

  /**
   * Obtener listado paginado de pagos ya realizados (pestaña "Pagados"), con filtro opcional de fechas
   * NOTA: pageNumber es 0-based (pageNumber=0 es la primera página).
   */
  getPaid(pageNumber: number = 0, pageSize: number = 10, from?: string, to?: string): Observable<ApiResponse<WorkbiePaidListResponseDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.PAID
    );

    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (from) {
      params = params.set('from', from);
    }
    if (to) {
      params = params.set('to', to);
    }

    return this.http.get<ApiResponse<WorkbiePaidListResponseDTO>>(url, { params });
  }

  /**
   * Obtener el detalle de TODOS los pagos pendientes de un workbie (usado desde la pestaña "Pendientes")
   */
  getPendingDetail(workbieUserId: string): Observable<ApiResponse<WorkbiePayoutDetailDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.DETAIL_PENDING,
      `/${workbieUserId}`
    );

    return this.http.get<ApiResponse<WorkbiePayoutDetailDTO>>(url);
  }

  /**
   * @deprecated Usar getPending() — se mantiene por compatibilidad con la vista semanal anterior
   * Obtener listado resumido de pagos pendientes semanales
   */
  getWeeklyPendingPayouts(pageNumber: number = 1, pageSize: number = 10): Observable<ApiResponse<WorkbiePayoutSummaryResponseDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.WEEKLY_PENDING
    );

    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ApiResponse<WorkbiePayoutSummaryResponseDTO>>(url, { params });
  }

  /**
   * @deprecated Usar getPendingDetail() desde la pestaña "Pendientes" del nuevo dashboard
   * Obtener detalle completo de pagos de un workbie específico
   */
  getPayoutDetailByWorkbie(workbieUserId: string): Observable<ApiResponse<WorkbiePayoutDetailDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.DETAIL,
      `/${workbieUserId}`
    );

    return this.http.get<ApiResponse<WorkbiePayoutDetailDTO>>(url);
  }

  /**
   * Procesar pago de workbie
   */
  processPayment(request: ProcessPaymentRequestDTO): Observable<ApiResponse<ProcessPaymentResponseDTO>> {
    const url = this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.WORKBIE_PAYOUTS.PROCESS_PAYMENT
    );

    return this.http.post<ApiResponse<ProcessPaymentResponseDTO>>(url, request);
  }
}
